/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * OrganizerAttachPanel — attaching runs, in place on the board.
 *
 * ON THE BOARD, NOT IN AN ORGANIZER SECTION. An organizer looking at their own
 * race should not have to leave it, find an admin area and look the race up
 * again. Same page, same URL as everybody else's; the panel simply is not there
 * for anybody else.
 *
 * NOTHING HERE DECIDES WHO MAY SEE IT. organizer_owns_race and the saved
 * queries behind the attach modals answer only to the caller who created the
 * race, so a visitor and an ordinary athlete get nothing and this draws
 * nothing at all. Do not add a role check here and treat it as the guard — the
 * server is the guard.
 *
 * THE ONE EXCEPTION IS `signedIn`, and it is not a permission check. It stops
 * the panel ASKING at all when nobody is logged in: the queries sit behind the
 * signed-in endpoint, and a 403 there raises a "Session expired" dialog over
 * the whole page. On a public board that dialog met every visitor.
 *
 * ONE CARD, ONE BUTTON. Both kinds of event attach in a modal: day by day for
 * a multi-day event (DailyAttachModal), one run per entrant for a race
 * (RaceAttachModal). The race's inline list read the race in one request the
 * server caps at 500 rows, so a 20,000-entrant race took forty rounds; the
 * modal pages from the server and attaches the whole race in one click.
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ownsRace } from './publishApi';
import { isMultiDayEvent } from './dailyMode';
import DailyAttachPanel from './DailyAttachPanel';
import RaceAttachModal from './RaceAttachModal';
import RemoveAttachedModal from './RemoveAttachedModal';

const INK = '#241708';
const MUTED = '#6B5844';
const FLAME = '#FF6A1A';
const HAIR = 'rgba(36, 23, 8, .10)';

/**
 * A multi-day event attaches many runs per entrant, day by day; a race takes
 * one run per entrant. Split here so neither has to know about the other.
 */
export default function OrganizerAttachPanel(props) {
  if (isMultiDayEvent(props.eventStart, props.eventEnd, props.eventTimezone)) {
    return <DailyAttachPanel {...props} />;
  }
  return <SingleRunAttachPanel {...props} />;
}

function SingleRunAttachPanel({
  api, eventActivityId, minDistanceMeters = 0, signedIn = false,
}) {
  const [open, setOpen] = useState(false);
  // The way back out. Its own dialog rather than a list inside the attach
  // report: a report only ever holds the LAST attach, so it was lost on close
  // and replaced by the next one — see RemoveAttachedModal's header.
  const [removeOpen, setRemoveOpen] = useState(false);

  const owner = useQuery({
    queryKey: ['board', 'owns-race', eventActivityId],
    queryFn: ({ signal }) => ownsRace(api, { eventActivityId, signal }),
    // NEVER ASK WITHOUT A SESSION — see the header.
    enabled: Number.isFinite(eventActivityId) && signedIn,
  });
  if (owner.data !== true) return null;

  return (
    <section aria-label="Attach runs to this race" style={{
      margin: '0 0 20px', boxSizing: 'border-box', padding: '18px 22px',
      border: `1px solid ${HAIR}`, borderRadius: 20, background: '#fff', color: INK,
      display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between',
    }}>
      <div>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Attach runs</h2>
        <p style={{ margin: '4px 0 0', fontSize: 14, color: MUTED }}>
          Give every entrant without a result their fastest run that covered the distance — in one go.
        </p>
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          type="button"
          onClick={() => setRemoveOpen(true)}
          aria-haspopup="dialog"
          style={{
            minHeight: 46, padding: '0 18px', borderRadius: 12, background: '#fff', color: INK,
            border: `1px solid ${HAIR}`, fontWeight: 700, fontSize: 15, cursor: 'pointer',
          }}
        >
          Attached results
        </button>
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          style={{
            minHeight: 46, padding: '0 22px', border: 0, borderRadius: 12, background: FLAME, color: '#fff',
            fontWeight: 700, fontSize: 15, cursor: 'pointer', boxShadow: '0 8px 20px -8px rgba(255,106,26,.7)',
          }}
        >
          Attach runs
        </button>
      </div>
      {open && (
        <RaceAttachModal
          api={api}
          eventActivityId={eventActivityId}
          minDistanceMeters={minDistanceMeters}
          onClose={() => setOpen(false)}
        />
      )}
      {removeOpen && (
        <RemoveAttachedModal
          api={api}
          eventActivityId={eventActivityId}
          onClose={() => setRemoveOpen(false)}
        />
      )}
    </section>
  );
}
